import {
  getWarningLevel,
  sortWarningsBySeverity,
} from '@src/shared/lib/warnings';
import type {
  AbsenceWarningItem,
  AbsenceWarningSummary,
  AbsenceWarnings,
} from '../types';

export type GroupedResource = {
  team?: unknown;
  warningSummary?: AbsenceWarningSummary | null;
};

/**
 * One warning per group, standing for the warnings of all its rows.
 *
 * Each kind of warning is listed once however many rows carry it, red before
 * amber, and the group is red as soon as one of its rows is. Groups without a
 * warning are left out.
 *
 * Where several rows carry a warning, it links to the first of them that names
 * an absence: a closed group then still leads to one place to start fixing.
 */
export function summarizeGroupWarnings(
  resources: readonly GroupedResource[],
): AbsenceWarnings<string> {
  const warningsByGroup = new Map<string, Map<string, AbsenceWarningItem>>();
  for (const resource of resources) {
    const warnings = resource.warningSummary?.warnings;
    if (!warnings?.length) continue;
    const groupId = String(resource.team ?? '');
    let unique = warningsByGroup.get(groupId);
    if (!unique) {
      unique = new Map();
      warningsByGroup.set(groupId, unique);
    }
    for (const warning of warnings) {
      const kept = unique.get(warning.code);
      if (!kept) {
        unique.set(warning.code, warning);
      } else if (!kept.target && warning.target) {
        unique.set(warning.code, { ...kept, target: warning.target });
      }
    }
  }

  const summaries: AbsenceWarnings<string> = new Map();
  for (const [groupId, unique] of warningsByGroup) {
    const warnings = sortWarningsBySeverity([...unique.values()]);
    summaries.set(groupId, {
      level: getWarningLevel(warnings),
      count: warnings.length,
      warnings,
    });
  }
  return summaries;
}
